/**
 * Sashigane Engine — Generator + Validator
 * Divide the board into 1-cell-wide L-shaped regions.
 * Each L has a numbered circle at its elbow and optional arrows at its endpoints.
 */

export type ArrowDir = 'U' | 'D' | 'L' | 'R';
export interface SCell { r: number; c: number; }

export interface SRegion {
  elbow: SCell;
  hDir: 'L' | 'R';
  hLen: number;  // cells beyond elbow in horizontal direction (>= 1)
  vDir: 'U' | 'D';
  vLen: number;  // cells beyond elbow in vertical direction (>= 1)
}

export function regionCells(reg: SRegion): SCell[] {
  const { r, c } = reg.elbow;
  const cells: SCell[] = [{ r, c }];
  for (let i = 1; i <= reg.hLen; i++)
    cells.push({ r, c: reg.hDir === 'R' ? c + i : c - i });
  for (let i = 1; i <= reg.vLen; i++)
    cells.push({ r: reg.vDir === 'D' ? r + i : r - i, c });
  return cells;
}

export function regionSize(reg: SRegion): number { return 1 + reg.hLen + reg.vLen; }

export function regionEndpoints(reg: SRegion): [SCell, SCell] {
  const { r, c } = reg.elbow;
  return [
    { r, c: reg.hDir === 'R' ? c + reg.hLen : c - reg.hLen },
    { r: reg.vDir === 'D' ? r + reg.vLen : r - reg.vLen, c },
  ];
}

export function arrowTowardElbow(ep: SCell, elbow: SCell): ArrowDir {
  if (ep.r === elbow.r) return ep.c < elbow.c ? 'R' : 'L';
  return ep.r < elbow.r ? 'D' : 'U';
}

export interface GridClue {
  r: number; c: number;
  kind: 'elbow' | 'arrow';
  num?: number;      // only for elbow with number clue
  dir?: ArrowDir;    // only for arrow clue
}

export interface SashiganePuzzle {
  n: number;
  seed: number;
  clues: GridClue[];
  regions: SRegion[];
  regionMap: number[][];  // regionMap[r][c] = region index
}

// ─── PRNG ─────────────────────────────────────────────────────────────────────

class PRNG {
  private s: number;
  constructor(seed: number) { this.s = (seed >>> 0) || 1; }
  next(): number {
    this.s = (Math.imul(1664525, this.s) + 1013904223) >>> 0;
    return this.s / 0x100000000;
  }
  int(lo: number, hi: number): number { return lo + Math.floor(this.next() * (hi - lo + 1)); }
  shuffle<T>(arr: T[]): void {
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(this.next() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
  }
}

// ─── Tiling Generator (backtracking) ──────────────────────────────────────────

function generateTiling(n: number, rng: PRNG): SRegion[] | null {
  const cov: boolean[][] = Array.from({ length: n }, () => Array(n).fill(false));
  const regions: SRegion[] = [];
  let steps = 0;

  function firstFree(): SCell | null {
    for (let r = 0; r < n; r++)
      for (let c = 0; c < n; c++)
        if (!cov[r][c]) return { r, c };
    return null;
  }

  function allFree(cells: SCell[]): boolean {
    return cells.every(({ r, c }) =>
      r >= 0 && r < n && c >= 0 && c < n && !cov[r][c]
    );
  }

  function mark(cells: SCell[], val: boolean) { cells.forEach(({ r, c }) => { cov[r][c] = val; }); }

  // Returns all valid L-shapes that include the cell (r,c) as one of their cells.
  // Because (r,c) is the first uncovered cell, cells above row r and cells left
  // of (r,c) in row r are covered — so three structural cases exist.
  function candidates(r: number, c: number): SRegion[] {
    const result: SRegion[] = [];

    const tryAdd = (reg: SRegion) => {
      if (allFree(regionCells(reg))) result.push(reg);
    };

    // Case 1: (r,c) is the elbow
    for (const hDir of ['L', 'R'] as const)
      for (const vDir of ['U', 'D'] as const)
        for (let hLen = 1; hLen < n; hLen++)
          for (let vLen = 1; vLen < n; vLen++)
            tryAdd({ elbow: { r, c }, hDir, hLen, vDir, vLen });

    // Case 2: (r,c) is in the horizontal arm; elbow to the right at (r, c+d)
    for (let d = 1; c + d < n; d++)
      for (const vDir of ['U', 'D'] as const)
        for (let vLen = 1; vLen < n; vLen++)
          tryAdd({ elbow: { r, c: c + d }, hDir: 'L', hLen: d, vDir, vLen });

    // Case 3: (r,c) is in the vertical arm; elbow below at (r+d, c)
    for (let d = 1; r + d < n; d++)
      for (const hDir of ['L', 'R'] as const)
        for (let hLen = 1; hLen < n; hLen++)
          tryAdd({ elbow: { r: r + d, c }, vDir: 'U', vLen: d, hDir, hLen });

    rng.shuffle(result);
    return result;
  }

  function backtrack(): boolean {
    if (++steps > 80000) return false;
    const fc = firstFree();
    if (!fc) return true;

    for (const reg of candidates(fc.r, fc.c)) {
      const cells = regionCells(reg);
      mark(cells, true);
      regions.push(reg);
      if (backtrack()) return true;
      mark(cells, false);
      regions.pop();
    }
    return false;
  }

  return backtrack() ? [...regions] : null;
}

// ─── Clue generation ──────────────────────────────────────────────────────────

const DIFFICULTY: Record<number, { showNum: number; showArrow: number }> = {
  1: { showNum: 1.0, showArrow: 1.0 },
  2: { showNum: 1.0, showArrow: 0.7 },
  3: { showNum: 0.8, showArrow: 0.6 },
  4: { showNum: 0.6, showArrow: 0.5 },
  5: { showNum: 0.4, showArrow: 0.3 },
};

function buildClues(regions: SRegion[], difficulty: number, rng: PRNG): GridClue[] {
  const cfg = DIFFICULTY[difficulty] ?? DIFFICULTY[1];
  const clues: GridClue[] = [];

  for (const reg of regions) {
    clues.push({
      r: reg.elbow.r, c: reg.elbow.c,
      kind: 'elbow',
      num: rng.next() < cfg.showNum ? regionSize(reg) : undefined,
    });
    for (const ep of regionEndpoints(reg)) {
      if (rng.next() < cfg.showArrow)
        clues.push({ r: ep.r, c: ep.c, kind: 'arrow', dir: arrowTowardElbow(ep, reg.elbow) });
    }
  }
  return clues;
}

// ─── Public generator ─────────────────────────────────────────────────────────

const GRID_SIZE: Record<number, number> = { 1: 4, 2: 5, 3: 6, 4: 7, 5: 8 };

export class SashiganeGenerator {
  generate(difficulty: number, seedParam?: number): SashiganePuzzle {
    const n = GRID_SIZE[difficulty] ?? 5;

    for (let attempt = 0; attempt < 300; attempt++) {
      const seed = seedParam !== undefined && attempt === 0
        ? seedParam
        : Math.floor(Math.random() * 900000 + 100000);

      const rng = new PRNG(seed);
      const regions = generateTiling(n, rng);
      if (!regions) continue;

      const regionMap: number[][] = Array.from({ length: n }, () => Array(n).fill(-1));
      regions.forEach((reg, idx) => {
        regionCells(reg).forEach(({ r, c }) => { regionMap[r][c] = idx; });
      });

      const clues = buildClues(regions, difficulty, rng);
      return { n, seed, clues, regions, regionMap };
    }
    throw new Error('No se pudo generar el puzzle Sashigane');
  }
}

// ─── Validation ───────────────────────────────────────────────────────────────

// Compute connected components from user-drawn borders.
// hBorders[r][c] = border below cell (r,c), i.e., between rows r and r+1
// vBorders[r][c] = border right of cell (r,c), i.e., between cols c and c+1
export function computeRegionMap(
  n: number,
  hBorders: boolean[][],
  vBorders: boolean[][],
): number[][] {
  const map: number[][] = Array.from({ length: n }, () => Array(n).fill(-1));
  let id = 0;
  for (let r = 0; r < n; r++) {
    for (let c = 0; c < n; c++) {
      if (map[r][c] !== -1) continue;
      const queue: SCell[] = [{ r, c }];
      map[r][c] = id;
      for (let qi = 0; qi < queue.length; qi++) {
        const { r: cr, c: cc } = queue[qi];
        if (cr > 0 && !hBorders[cr - 1][cc] && map[cr - 1][cc] === -1)
          { map[cr - 1][cc] = id; queue.push({ r: cr - 1, c: cc }); }
        if (cr < n - 1 && !hBorders[cr][cc] && map[cr + 1][cc] === -1)
          { map[cr + 1][cc] = id; queue.push({ r: cr + 1, c: cc }); }
        if (cc > 0 && !vBorders[cr][cc - 1] && map[cr][cc - 1] === -1)
          { map[cr][cc - 1] = id; queue.push({ r: cr, c: cc - 1 }); }
        if (cc < n - 1 && !vBorders[cr][cc] && map[cr][cc + 1] === -1)
          { map[cr][cc + 1] = id; queue.push({ r: cr, c: cc + 1 }); }
      }
      id++;
    }
  }
  return map;
}

export function checkLShape(cells: SCell[]): { valid: boolean; elbow?: SCell } {
  if (cells.length < 3) return { valid: false };
  const set = new Set(cells.map(({ r, c }) => `${r},${c}`));

  const elbows = cells.filter(({ r, c }) => {
    const h = set.has(`${r},${c - 1}`) || set.has(`${r},${c + 1}`);
    const v = set.has(`${r - 1},${c}`) || set.has(`${r + 1},${c}`);
    return h && v;
  });
  if (elbows.length !== 1) return { valid: false };

  const elbow = elbows[0];
  const { r: er, c: ec } = elbow;
  const hCells = cells.filter(({ r, c }) => r === er && c !== ec);
  const vCells = cells.filter(({ r, c }) => c === ec && r !== er);

  if (hCells.length + vCells.length + 1 !== cells.length) return { valid: false };
  if (hCells.length === 0 || vCells.length === 0) return { valid: false };

  const allHRight = hCells.every(({ c }) => c > ec);
  const allHLeft  = hCells.every(({ c }) => c < ec);
  if (!allHRight && !allHLeft) return { valid: false };

  const allVDown = vCells.every(({ r }) => r > er);
  const allVUp   = vCells.every(({ r }) => r < er);
  if (!allVDown && !allVUp) return { valid: false };

  // Check contiguity
  const hCols = hCells.map(({ c }) => c).sort((a, b) => a - b);
  if (allHRight) { for (let i = 0; i < hCols.length; i++) if (hCols[i] !== ec + i + 1) return { valid: false }; }
  else           { for (let i = 0; i < hCols.length; i++) if (hCols[hCols.length - 1 - i] !== ec - i - 1) return { valid: false }; }

  const vRows = vCells.map(({ r }) => r).sort((a, b) => a - b);
  if (allVDown) { for (let i = 0; i < vRows.length; i++) if (vRows[i] !== er + i + 1) return { valid: false }; }
  else          { for (let i = 0; i < vRows.length; i++) if (vRows[vRows.length - 1 - i] !== er - i - 1) return { valid: false }; }

  return { valid: true, elbow };
}

export function validateUserSolution(
  n: number,
  hBorders: boolean[][],
  vBorders: boolean[][],
  puzzle: SashiganePuzzle,
): boolean {
  const userMap = computeRegionMap(n, hBorders, vBorders);

  const groups = new Map<number, SCell[]>();
  for (let r = 0; r < n; r++)
    for (let c = 0; c < n; c++) {
      const id = userMap[r][c];
      if (!groups.has(id)) groups.set(id, []);
      groups.get(id)!.push({ r, c });
    }

  const clueMap = new Map<string, GridClue>();
  puzzle.clues.forEach(cl => clueMap.set(`${cl.r},${cl.c}`, cl));

  for (const [, cells] of groups) {
    const { valid, elbow } = checkLShape(cells);
    if (!valid || !elbow) return false;

    for (const { r, c } of cells) {
      const cl = clueMap.get(`${r},${c}`);
      if (!cl) continue;
      if (cl.kind === 'elbow') {
        if (cl.r !== elbow.r || cl.c !== elbow.c) return false;
        if (cl.num !== undefined && cells.length !== cl.num) return false;
      } else {
        const neighborCount = cells.filter(o => Math.abs(o.r - r) + Math.abs(o.c - c) === 1).length;
        if (neighborCount !== 1) return false;
        if (arrowTowardElbow({ r, c }, elbow) !== cl.dir) return false;
      }
    }
  }
  return true;
}

// Solution border masks (for assisted mode)
export function solutionBorders(puzzle: SashiganePuzzle): { h: boolean[][]; v: boolean[][] } {
  const { n, regionMap } = puzzle;
  const h = Array.from({ length: n - 1 }, (_, r) =>
    Array.from({ length: n }, (_, c) => regionMap[r][c] !== regionMap[r + 1][c])
  );
  const v = Array.from({ length: n }, (_, r) =>
    Array.from({ length: n - 1 }, (_, c) => regionMap[r][c] !== regionMap[r][c + 1])
  );
  return { h, v };
}
